import { Button, Input } from 'components/atoms';
import ImageInputFormField from 'components/atoms/inputs/imageForm/ImageInputForm';
import { FormInput } from 'components/molecules';
import DataWrapper from 'components/molecules/data-wrapper/DataWrapper';
import ViewHint from 'components/molecules/hint/ViewHint';
import CopyrightPopupForm from 'components/molecules/popup/copyright-select/CopyrightSelectPopupForm';
import GenrePopupForm from 'components/molecules/popup/genre-select/GenreSelectPopupForm';
import LanguageSelectPopupForm from 'components/molecules/popup/language-select/LanguageSelectPopupForm';
import ReleaseTypeSelectPopupForm from 'components/molecules/popup/release-type-select/ReleaseTypeSelectPopupForm';
import YearSelectPopupForm from 'components/molecules/popup/year-select/YearSelectPopupForm';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCopyrightList } from 'services/account';
import { useGetGenres, useGetLanguages } from 'services/enums';
import { RELEASE_TYPE } from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { GENRE, STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';

const MusicProductDigitalData = () => {
  const { t } = useTranslation();
  const { getSelectOptions } = useEnumToSelectOptions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const languages = useGetLanguages();

  const copyrightOptions = useGetCopyrightList();
  // const generateEan = useGenerateEan();

  const genres = useGetGenres();
  const {
    values,
    setFieldValue,
    status,
    initialValues,
    errors,
    submitCount,
  }: IFormikContextType<IMusicProductDigitalForm> = useFormikContext<IMusicProductDigitalForm>();

  const { setForm } = useStoreState();

  const onGoToStep = (step: string) => {
    setForm('musicProductDigitalForm', values);
    navigate(`/music-product-digital/${values.id ? `edit/${values.id}` : 'create'}/${step}`);
  };

  const showAssignEanSelect = !status?.displayMode;
  const showEanInput = !values.assignEan || status?.displayMode;
  return (
    <StepsTab
      nextName="artists"
      onNext={() => onGoToStep('artists')}
      backName="tracks"
      onBack={() => onGoToStep('tracks')}
    >
      <DataWrapper>
        <ViewHint hintId="music-product-digital-data-title" />

        <div className="h-[200px] w-[200px]">
          <ImageInputFormField<IMusicProductDigitalForm>
            nameCropped="file"
            nameOriginal="fileOriginal"
            draftKey="musicProductDigital.profilePicture"
            fullResolutionUrl={values.productImageLink}
            previewUrl={values.productImagePreviewLink}
            imageSize={{ width: 1500, height: 1500 }}
            editable={!status?.displayMode}
            onDelete={
              initialValues.productImageLink
                ? (action) => {
                    setFieldValue('productImageLink', '');
                    setFieldValue('productImagePreviewLink', '');
                    setFieldValue('file', null);
                    setFieldValue('fileOriginal', null);
                    action.close();
                  }
                : undefined
            }
          />
        </div>
        <FormInput.Text required name="mainTitle.title" label={t('input.label.title')} autoFocus />
        <FormInput.Text name="mainTitle.version" label={t('input.label.version')} />
        <LanguageSelectPopupForm
          onSelectLanguage={(language, actions) => {
            setFieldValue('mainTitle.language', language?.id ?? null);
            actions.close();
          }}
          initialLanguage={languages.data?.find((l) => l.id === values.mainTitle.language)}
          displayMode={status?.displayMode}
          trigger={
            <div className="flex w-full cursor-pointer">
              <Input.Text
                required
                name=""
                error={submitCount > 0 ? errors.mainTitle?.language : undefined}
                label={t('input.label.language')}
                value={languages.data?.find((l) => l.id === values.mainTitle.language)?.name ?? ''}
                style={{ pointerEvents: 'none' }}
              />
            </div>
          }
        />

        <div />
        <ViewHint hintId="music-product-digital-data-release" />

        <ReleaseTypeSelectPopupForm
          onSelectReleaseType={(releaseType, actions) => {
            setFieldValue('releaseType', releaseType ?? null);
            actions.close();
          }}
          initialReleaseType={values.releaseType}
          displayMode={status?.displayMode}
          trigger={
            <div className="flex w-full cursor-pointer">
              <Input.Text
                required
                name=""
                error={submitCount > 0 ? errors.releaseType : undefined}
                label={t('input.label.releaseType')}
                value={
                  values.releaseType !== -1 && values.releaseType !== null
                    ? t(`release-type.${RELEASE_TYPE[values.releaseType]}.name`)
                    : ''
                }
                style={{ pointerEvents: 'none' }}
              />
            </div>
          }
        />

        <FormInput.Text
          required
          type="date"
          name="releaseDate"
          label={t('input.label.releaseDate')}
        />

        <GenrePopupForm
          onSelectGenre={(genre, actions) => {
            setFieldValue('genre.id', genre ?? null);
            actions.close();
          }}
          initialGenre={values.genre.id}
          displayMode={status?.displayMode}
          trigger={
            <div className="flex w-full cursor-pointer">
              <Input.Text
                required
                name=""
                error={submitCount > 0 ? errors.genre?.id : undefined}
                label={t('input.label.genre')}
                value={
                  !!values.genre.id && values.genre.id !== -1
                    ? t(`genre.${GENRE[values.genre.id]}.name`)
                    : ''
                }
                style={{ pointerEvents: 'none' }}
              />
            </div>
          }
        />

        <div />
        <ViewHint hintId="music-product-digital-data-ean" />
        {!status?.displayMode && (
          <div
            className="flex  w-full p-12 rounded-sm gap-4"
            style={{ backgroundColor: 'var(--background)' }}
          >
            <Button
              fullWidth
              variant={
                values.assignEan === false ? BUTTON_VARIANT.submit : BUTTON_VARIANT.background
              }
              onClick={() => setFieldValue('assignEan', false)}
            >
              <Button.Text text={t('input.option.yes')} />
            </Button>
            <Button
              fullWidth
              variant={
                values.assignEan === true ? BUTTON_VARIANT.submit : BUTTON_VARIANT.background
              }
              onClick={() => setFieldValue('assignEan', true)}
            >
              <Button.Text text={t('input.option.no')} />
            </Button>
          </div>
        )}
        <FormInput.Text
          required={!values.assignEan}
          name="ean"
          label={t('input.label.ean')}
          type="number"
          disabled={values.assignEan}
        />
        <div />
        <ViewHint hintId="music-product-digital-data-copyrights" />
        <div className="flex w-full gap-2">
          <YearSelectPopupForm
            onSelectYear={(genre, actions) => {
              setFieldValue('pLine.year', genre ?? null);
              actions.close();
            }}
            initialYear={typeof values.pLine?.year === 'number' ? values.pLine?.year : null}
            displayMode={status?.displayMode}
            trigger={
              <div className="flex w-full max-w-40 cursor-pointer">
                <Input.Text
                  required
                  name=""
                  error={submitCount > 0 ? (errors.pLine as any)?.year : undefined}
                  label={t('input.label.year')}
                  value={values.pLine?.year ?? ''}
                  style={{ pointerEvents: 'none' }}
                />
              </div>
            }
          />
          <CopyrightPopupForm
            onSelectCopyright={(name, actions) => {
              setFieldValue('pLine.name', name ?? null);
              actions.close();
            }}
            initialCopyright={values.pLine?.name ?? null}
            displayMode={status?.displayMode}
            mode="pline"
            trigger={
              <div className="flex w-full cursor-pointer">
                <Input.Text
                  required
                  name=""
                  error={submitCount > 0 ? (errors.pLine as any)?.name : undefined}
                  label={t('input.label.pline')}
                  value={values.pLine?.name ?? ''}
                  style={{ pointerEvents: 'none' }}
                  titleEnabled
                />
              </div>
            }
          />
        </div>
        <div className="flex w-full gap-2">
          <YearSelectPopupForm
            onSelectYear={(genre, actions) => {
              setFieldValue('cLine.year', genre ?? null);
              actions.close();
            }}
            initialYear={typeof values.cLine?.year === 'number' ? values.cLine?.year : null}
            displayMode={status?.displayMode}
            trigger={
              <div className="flex w-full max-w-40 cursor-pointer">
                <Input.Text
                  required
                  name=""
                  error={submitCount > 0 ? (errors.cLine as any)?.year : undefined}
                  label={t('input.label.year')}
                  value={values.cLine?.year ?? ''}
                  style={{ pointerEvents: 'none' }}
                />
              </div>
            }
          />
          <CopyrightPopupForm
            onSelectCopyright={(name, actions) => {
              setFieldValue('cLine.name', name ?? null);
              actions.close();
            }}
            initialCopyright={values.cLine?.name ?? null}
            displayMode={status?.displayMode}
            mode="cline"
            trigger={
              <div className="flex w-full cursor-pointer">
                <Input.Text
                  required
                  name=""
                  error={submitCount > 0 ? (errors.cLine as any)?.name : undefined}
                  label={t('input.label.cline')}
                  value={values.cLine?.name ?? ''}
                  style={{ pointerEvents: 'none' }}
                  titleEnabled
                />
              </div>
            }
          />
        </div>
        <div />
        {!!status?.displayMode && <ViewHint hintId={STATUS[values.releaseStatus]} />}
      </DataWrapper>
    </StepsTab>
  );
};

export default MusicProductDigitalData;
